import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import api from "../api/api";
import SideNavLayout from "../customsidenav/sidenavlayout";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete'; // Add this import
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ToastMessage from "../utils/mui/righttoptoast";
import { getPracticeId } from "../config/practice";
import {
  ALL,
  PAGE_SIZES,
  clampPage,
  effectiveLimit,
  filterPrices,
  pageCount,
  pageOfIndex,
  pageSlice,
  pricesCsv,
} from "./pricePaging";

dayjs.extend(utc);
dayjs.extend(timezone);

// Every element that is scrolled right now (the window and any scrolled
// ancestor of the list), so a refresh after Save can put them back.
const scrollSnapshot = (el) => {
  const spots = [];
  for (let n = el?.parentElement; n; n = n.parentElement) {
    if (n.scrollTop || n.scrollLeft) spots.push([n, n.scrollTop, n.scrollLeft]);
  }
  return { spots, x: window.scrollX || 0, y: window.scrollY || 0 };
};
const restoreScroll = (snap) => {
  if (!snap) return;
  for (const [n, top, left] of snap.spots) {
    n.scrollTop = top;
    n.scrollLeft = left;
  }
  try {
    if ((window.scrollX || 0) !== snap.x || (window.scrollY || 0) !== snap.y) window.scrollTo(snap.x, snap.y);
  } catch (e) {
    /* jsdom: scrollTo is not implemented */
  }
};

// Prev / page / Next and Rows per page. Rendered above AND below the table
// from the same state, so both always agree.
const PricePager = ({ position, page, limit, totalCount, totalPages, onPage, onLimit }) => {
  const size = effectiveLimit(limit, totalCount);
  const first = totalCount === 0 ? 0 : (page - 1) * size + 1;
  const last = Math.min(page * size, totalCount);
  return (
    <div
      className={`flex flex-wrap justify-between items-center gap-3 px-6 py-3 ${position === "top" ? "border-b" : ""}`}
      data-testid={`prices-pager-${position}`}
    >
      <div className="text-sm text-gray-600">
        Showing {first} to {last} of {totalCount}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          Rows per page
          <select
            aria-label={`Rows per page (${position})`}
            value={limit}
            onChange={(e) => onLimit(Number(e.target.value))}
            className="border rounded px-2 py-1"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n === ALL ? "All" : n}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={`Previous page (${position})`}
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Prev
          </button>
          <div className="px-4 py-1 bg-black text-white rounded" aria-label={`Page (${position})`}>
            {page}
          </div>
          <span className="text-sm text-gray-600 whitespace-nowrap">of {Math.max(totalPages, 1)}</span>
          <button
            type="button"
            aria-label={`Next page (${position})`}
            disabled={page >= totalPages}
            onClick={() => onPage(page + 1)}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// The header "select all" box: ticked when every row on the page is selected,
// a dash when only some are.
const PageCheckbox = ({ checked, indeterminate, disabled, onChange }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label="Select all on this page"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className="cursor-pointer"
    />
  );
};

const Prices = () => {
  const practiseid = getPracticeId();

  const [lookupdata, setLookupdata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [highlightId, setHighlightId] = useState(null);
  const [updateLookup, setUpdateLookup] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentAction, setCurrentAction] = useState('edit'); // 'edit' or 'delete'

  // Toast states
  const [toastSeverity, setToastSeverity] = useState('success');
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const listRef = useRef(null);
  const pendingScroll = useRef(null);

  const handleToastClose = () => {
    setOpenToast(false);
  };

  // Helper function for toast notifications
  const showToast = (message, severity = 'success') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setOpenToast(true);
  };

  // Loads the whole list. Page, rows per page, search and selection are NOT
  // touched here (11-04: a Save used to land the user back on page 1 because
  // the search effect reset the page on every data change).
  const fetchdata = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/v1/${practiseid}/lookup_master/get`);
      // No rows comes back as data: {} (status 201), not [].
      const data = Array.isArray(response?.data) ? response.data : [];
      setLookupdata(data);
      return data;
    } catch (error) {
      console.log(error);
      showToast('Failed to fetch data', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredLogs = useMemo(() => filterPrices(lookupdata, search), [lookupdata, search]);
  const totalCount = filteredLogs.length;
  const totalPages = pageCount(totalCount, limit);
  const logs = useMemo(() => pageSlice(filteredLogs, page, limit), [filteredLogs, page, limit]);

  // A delete (or a refresh with fewer rows) can leave the page past the end.
  useEffect(() => {
    const p = clampPage(page, totalCount, limit);
    if (p !== page) setPage(p);
  }, [page, totalCount, limit]);

  // Selected ids that are no longer in the list (deleted) drop out.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(lookupdata.map((it) => it.id));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [lookupdata]);

  // Put the scroll back once the refreshed rows are on screen.
  useLayoutEffect(() => {
    if (!loading && pendingScroll.current) {
      const snap = pendingScroll.current;
      pendingScroll.current = null;
      restoreScroll(snap);
    }
  }, [loading, logs]);

  useEffect(() => {
    if (highlightId === null) return undefined;
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [highlightId]);

  const goToPage = (p) => setPage(clampPage(p, totalCount, limit));
  const changeLimit = (n) => {
    setLimit(n);
    setPage(1);
  };
  const changeSearch = (value) => {
    setSearch(value);
    setPage(1);
    // A selection made under another search would hide rows the user can't see.
    setSelectedIds(new Set());
  };

  // SELECTION
  const pageIds = logs.map((it) => it.id);
  const pageSelectedCount = pageIds.filter((id) => selectedIds.has(id)).length;
  const pageAllSelected = pageIds.length > 0 && pageSelectedCount === pageIds.length;
  const allMatchingSelected = totalCount > 0 && filteredLogs.every((it) => selectedIds.has(it.id));
  const selectedRows = useMemo(() => lookupdata.filter((it) => selectedIds.has(it.id)), [lookupdata, selectedIds]);

  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  // Every row matching the search, across all pages (the list is already loaded
  // in full, so no extra call is needed for the ids).
  const selectAllMatching = () => setSelectedIds(new Set(filteredLogs.map((it) => it.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const exportSelected = () => {
    if (selectedRows.length === 0) return;
    const csv = pricesCsv(selectedRows);
    try {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prices-${dayjs().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      console.log(e);
      showToast('Export failed', 'error');
    }
  };

  // Refresh after a write, keeping page, rows per page, search and scroll.
  const refreshInPlace = async () => {
    pendingScroll.current = scrollSnapshot(listRef.current);
    const data = await fetchdata();
    if (!data) pendingScroll.current = null;
    return data;
  };


    // Handle create Click
  const handleCreate = () => {
    setSelectedItem({ context: '', content: '', insurance_cost: '', selfpay_cost: '' });
    setCurrentAction('insert');
    setUpdateLookup(true);
  };

  // Handle Edit Click
  const handleEdit = (item) => {
    setSelectedItem({ ...item });
    setCurrentAction('edit');
    setUpdateLookup(true);
  };

  // Handle Delete Click
  const handleDelete = (item) => {
    setSelectedItem(item);
    setCurrentAction('delete');
    setUpdateLookup(true);
  };

  // Handle Insert
  const handleInsert = async () => {
    const content = selectedItem.content;
    try {
      setLoading(true);
      const response = await api.post(`/v1/${practiseid}/lookup_master/insert`, {
        context: selectedItem.context,
        content: selectedItem.content,
        insurance_cost: isNaN(parseFloat(selectedItem.insurance_cost)) ? 0.00 : parseFloat(selectedItem.insurance_cost).toFixed(2),
        selfpay_cost: isNaN(parseFloat(selectedItem.selfpay_cost)) ? 0.00 : parseFloat(selectedItem.selfpay_cost).toFixed(2),
      });

      if (response.status === 200) {
        showToast('Price has been created successfully!');
        closeModal();
        const data = await fetchdata(); // Refresh data
        // Go to the page the new price is on (if the search still shows it).
        const added = (data || []).find((it) => it.content === content);
        if (added) {
          const i = filterPrices(data, search).findIndex((it) => it.id === added.id);
          if (i >= 0) {
            setPage(pageOfIndex(i, limit, filterPrices(data, search).length));
            setHighlightId(added.id);
          }
        }
      } else {
        showToast('Failed to insert', 'error');
      }
    } catch (error) {
      console.log(error);
      showToast('Insert failed', 'error');
    } finally {
      setLoading(false);
    }
  };


  // Handle Save/Update
  const handleSave = async () => {
    const id = selectedItem.id;
    try {
      setLoading(true);
      const response = await api.post(`/v1/${practiseid}/lookup_master/update/${selectedItem.id}`, {
        id: selectedItem.id,
        insurance_cost: isNaN(parseFloat(selectedItem.insurance_cost)) ? 0.00 : parseFloat(selectedItem.insurance_cost).toFixed(2),
        selfpay_cost: isNaN(parseFloat(selectedItem.selfpay_cost)) ? 0.00 : parseFloat(selectedItem.selfpay_cost).toFixed(2),
      });

      if (response.status === 200) {
        showToast('Updated successfully!');
        closeModal();
        await refreshInPlace(); // Refresh data, same page
        setHighlightId(id);
      } else {
        showToast('Failed to update', 'error');
      }
    } catch (error) {
      console.log(error);
      showToast('Update failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete Confirmation
  const handleDeleteConfirmed = async () => {
    try {
      setLoading(true);
      const response = await api.delete(`/v1/${practiseid}/lookup_master/${selectedItem.id}`);

      if (response.status === 200) {
        showToast('Deleted successfully!');
        closeModal();
        await refreshInPlace(); // same page (or the new last page)
      } else {
        showToast('Failed to delete', 'error');
      }
    } catch (error) {
      console.log(error);
      showToast('Delete failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Close modal and reset
  const closeModal = () => {
    setUpdateLookup(false);
    setSelectedItem(null);
    setCurrentAction('edit');
  };


  const orderTypes = [
    { value: "labs", label: "Labs" },
    { value: "diagnosis", label: "Diagnosis" },
    { value: "injection", label: "Injection" },
    { value: "mrecord", label: "Medical Record" },
    { value: "procedure", label: "Procedure" },
    { value: "vitamin", label: "Vitamin" }
  ];

  const pagerProps = { page, limit, totalCount, totalPages, onPage: goToPage, onLimit: changeLimit };

  return (
    <SideNavLayout>
      <div className="p-6">
        <div className="bg-white rounded-xl shadow border border-gray-200" ref={listRef}>
          {/* Header */}
          <div className="flex flex-wrap justify-between items-center gap-2 px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-800">Price details</h2>
            <div className="flex items-center w-full sm:w-auto">
              <input
                type="text"
                placeholder="Enter to search ..."
                value={search}
                onChange={(e) => changeSearch(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm flex-1 min-w-0 sm:flex-none sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <ControlPointIcon className="m-2 cursor-pointer" onClick={() => handleCreate()} titleAccess="Create New Price"/>
            </div>
          </div>

          {/* Pagination (top) */}
          <PricePager position="top" {...pagerProps} />

          {/* Selection bar */}
          {selectedIds.size > 0 && (
            <div
              className="flex flex-wrap items-center gap-x-4 gap-y-1 px-6 py-2 text-sm bg-blue-50 border-b"
              data-testid="prices-selection-bar"
            >
              <span className="font-medium text-gray-800">
                {allMatchingSelected
                  ? `All ${totalCount} item${totalCount === 1 ? "" : "s"}${search ? " matching the search" : ""} are selected.`
                  : pageAllSelected && selectedIds.size === pageIds.length
                    ? `All ${pageIds.length} on this page are selected.`
                    : `${selectedIds.size} selected.`}
              </span>
              {!allMatchingSelected && totalCount > pageIds.length && (
                <button type="button" className="text-blue-700 underline" onClick={selectAllMatching}>
                  Select all {totalCount} items
                </button>
              )}
              <button type="button" className="text-blue-700 underline" onClick={exportSelected}>
                Export selected (CSV)
              </button>
              <button type="button" className="text-gray-600 underline" onClick={clearSelection}>
                Clear selection
              </button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="border-b text-gray-600 bg-gray-50">
                <tr>
                  <th className="pl-6 pr-2 py-3 w-8">
                    <PageCheckbox
                      checked={pageAllSelected}
                      indeterminate={pageSelectedCount > 0 && !pageAllSelected}
                      disabled={pageIds.length === 0}
                      onChange={togglePage}
                    />
                  </th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Content</th>
                  <th className="px-6 py-3 pl-[4%]">Insurance Cost</th>
                  <th className="px-6 py-3">Selfpay Cost</th>
                  <th className="px-6 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((item) => (
                  <tr
                    key={item.id}
                    className={`border-b hover:bg-gray-50 ${item.id === highlightId ? "bg-yellow-50" : ""} ${selectedIds.has(item.id) ? "bg-blue-50" : ""}`}
                  >
                    <td className="pl-6 pr-2 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${item.content}`}
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleRow(item.id)}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-3 font-semibold text-gray-800">{item.context}</td>
                    <td className="px-6 py-3 font-semibold text-gray-800">{item.content}</td>
                    <td className="px-6 py-3">
                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs">
                        ${parseFloat(item.insurance_cost || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs">
                        ${parseFloat(item.selfpay_cost || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-3 flex gap-2">
                      <EditIcon
                        className="w-5 h-5 text-blue-500 cursor-pointer hover:text-blue-700"
                        onClick={() => handleEdit(item)}
                        titleAccess="Edit"
                      />
                      <DeleteIcon
                        className="w-5 h-5 text-red-500 cursor-pointer hover:text-red-700"
                        onClick={() => handleDelete(item)}
                        titleAccess="Delete"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {logs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No price details found
            </div>
          )}

          {/* Pagination (bottom) */}
          <PricePager position="bottom" {...pagerProps} />
        </div>

        <ToastMessage
          message={toastMessage}
          severity={toastSeverity}
          open={openToast}
          handleClose={handleToastClose}
        />
      </div>

      {/* Loader */}
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Edit/Delete Modal */}
      {updateLookup && selectedItem && (
        <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50">
          <div className="bg-white pb-3 rounded-lg shadow-md w-[40%] max-w-md">
            <div className="bg-[#E179545E] p-2 py-3">
              <div className="w-full flex items-center justify-between px-2">
                <span className="ml-2 font-semibold text-[#1D2951] text-base">
                  {currentAction === 'insert' ? 'Add Price' : currentAction === 'edit' ?  'Edit Price' : 'Delete Price'}
                </span>
                <button 
                  className="text-[#1D2951] hover:text-gray-700 text-xl leading-none mr-1" 
                  onClick={closeModal}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4">
              { currentAction === 'insert' ? (
                // Insert Form
                <>

              {/* Price Type Dropdown */}
              <div className="my-4">
                <label className="block text-sm font-semibold text-start text-gray-700">
                  Type
                </label>
                <select
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={selectedItem?.context || ''}
                  onChange={(e) =>
                    setSelectedItem({
                      ...selectedItem,
                      context: e.target.value,
                    })
                  }
                >
                  <option value="" disabled>
                    Select Price Type
                  </option>
                  {orderTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

                  <div className="my-4">
                    <label className="block text-sm font-semibold text-start mb-2">Content</label>
                    <input

                      type="text"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.content}    
                      onChange={(e) =>
                        setSelectedItem({ ...selectedItem, content: e.target.value })
                      }
                    />
                  </div>
                  <div className="my-4">  
                    <label className="block text-sm font-semibold text-start mb-2">Insurance Cost ($)</label>
                    <input
                      type="number"
                      step="0.01" 
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.insurance_cost || ''} 
                      onChange={(e) =>
                        setSelectedItem({
                          ...selectedItem,
                          insurance_cost: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-start mb-2">Selfpay Cost ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.selfpay_cost || ''}
                      onChange={(e) =>
                        setSelectedItem({
                          ...selectedItem,
                          selfpay_cost: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              ) :
              currentAction === 'edit' ? (
                // Edit Form
                <>
                  <div className="my-4">
                    <label className="block text-sm font-semibold text-center text-gray-700">
                      {selectedItem.context}: {selectedItem.content}
                    </label>
                  </div>
                  <div className="my-4">
                    <label className="block text-sm font-semibold text-start mb-2">Insurance Cost ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.insurance_cost || ''}
                      onChange={(e) =>
                        setSelectedItem({
                          ...selectedItem,
                          insurance_cost: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-start mb-2">Selfpay Cost ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.selfpay_cost || ''}
                      onChange={(e) =>
                        setSelectedItem({
                          ...selectedItem,
                          selfpay_cost: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              ) : (
                // Delete Confirmation
                <div className="text-center py-8">
                  <div className="text-lg font-semibold text-gray-800 mb-2">
                    Delete "{selectedItem.content}"?
                  </div>
                  <div className="text-sm text-gray-600 mb-6">
                    This action cannot be undone.
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 bg-gray-300 hover:bg-gray-400 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={currentAction === 'insert' ? handleInsert : currentAction === 'edit' ? handleSave : handleDeleteConfirmed}
                  disabled={loading}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    currentAction === 'edit'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Processing...' : (currentAction === 'insert' ? 'Save' : currentAction === 'edit' ? 'Save Changes' : 'Delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SideNavLayout>
  );
};

export default Prices;